import React, { useEffect, useMemo, useState } from 'react';
import { LuSquare } from 'react-icons/lu';

import { GUID, Strategy, StrategyType, StrategyWithUsage } from '@ndvr/portfolio-api';

import { AssetInfo } from '@/cip/_openapi/portfolio-optimizer';
import { useAssetsByIds } from '@/hooks/market-data-queries';

import { LibrarySortKey } from '../LibraryFilterBar';
import { LibraryPagination } from '../LibraryPagination';
import { extractSecurityIdentifiers, isBlackrockStrategy } from './strategy-utils';
import { StrategyCard } from './StrategyCard';

interface StrategiesSectionProps {
    strategies: StrategyWithUsage[];
    isLoading: boolean;
    hasSupportRole?: boolean;
    sort: LibrarySortKey;
    selectedGUIDs: Set<GUID<Strategy>>;
    onToggleSelect: (strategyGUID: GUID<Strategy>) => void;
    onEdit: (strategy: Strategy) => void;
    onClone: (strategy: Strategy) => void;
    onDelete: (strategy: StrategyWithUsage) => void;
}

const DEFAULT_PAGE_SIZE = 6;

export const StrategiesSection: React.FC<StrategiesSectionProps> = ({
    strategies,
    isLoading,
    hasSupportRole,
    sort,
    selectedGUIDs,
    onToggleSelect,
    onEdit,
    onClone,
    onDelete,
}) => {
    const [pageIndex, setPageIndex] = useState(0);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

    useEffect(() => {
        setPageIndex(0);
    }, [strategies, sort]);

    const sortedStrategies = [...strategies].sort((a, b) =>
        sort === 'accounts'
            ? (b.referencingAllocationCount ?? 0) - (a.referencingAllocationCount ?? 0)
            : a.strategyName.localeCompare(b.strategyName),
    );
    const pagedStrategies = sortedStrategies.slice(pageIndex * pageSize, (pageIndex + 1) * pageSize);

    const securityIdentifiers = useMemo(() => {
        const allAssetAllocations = pagedStrategies.flatMap((strategy) => strategy.assetAllocations);
        return extractSecurityIdentifiers(allAssetAllocations);
    }, [pagedStrategies]);

    const { data: assets = [] } = useAssetsByIds(securityIdentifiers);

    const getAssetInfo = (securityId: string): AssetInfo | undefined => assets.find((a) => a.sId === securityId);

    const canEditStrategy = (strategy: StrategyWithUsage) =>
        strategy.availableForEdits &&
        (strategy.strategyType === StrategyType.PERSONAL ||
            (strategy.strategyType === StrategyType.SHARED && !!hasSupportRole));

    const getDeleteBlockedReason = (strategy: StrategyWithUsage): string | undefined => {
        if (!strategy.availableForEdits) {
            return `${isBlackrockStrategy(strategy) ? 'BlackRock' : 'Claro'}-managed strategies can't be deleted`;
        }
        if (strategy.strategyType === StrategyType.SHARED && !hasSupportRole) {
            return 'Only support users can delete shared strategies';
        }
        const referencingAllocationCount = strategy.referencingAllocationCount ?? 0;
        if (referencingAllocationCount > 0) {
            return `Used by ${referencingAllocationCount} ${referencingAllocationCount === 1 ? 'model' : 'models'} — remove references first`;
        }
        return undefined;
    };

    if (sortedStrategies.length === 0) {
        return null;
    }

    return (
        <div>
            <div className="mb-3.5 flex flex-wrap items-center gap-2.5">
                <LuSquare className="text-cocoa-500 h-4.5 w-4.5" />
                <h2 className="text-cocoa-1000 text-xs font-semibold tracking-[0.08em] uppercase">Strategies</h2>
                <span className="text-cocoa-500 text-xs">{sortedStrategies.length}</span>
                <div className="bg-cocoa-200 h-px flex-1" />
                <LibraryPagination
                    pageIndex={pageIndex}
                    pageSize={pageSize}
                    totalItems={sortedStrategies.length}
                    onPageIndexChange={setPageIndex}
                    onPageSizeChange={(size) => {
                        setPageSize(size);
                        setPageIndex(0);
                    }}
                />
            </div>

            {isLoading && <div className="text-cocoa-500 text-sm">Loading strategies...</div>}
            {!isLoading && (
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3">
                    {pagedStrategies.map((strategy) => (
                        <StrategyCard
                            key={strategy.strategyGUID}
                            strategy={strategy}
                            canEdit={canEditStrategy(strategy)}
                            selected={selectedGUIDs.has(strategy.strategyGUID)}
                            deleteBlockedReason={getDeleteBlockedReason(strategy)}
                            getAssetInfo={getAssetInfo}
                            onToggleSelect={() => onToggleSelect(strategy.strategyGUID)}
                            onEdit={() => onEdit(strategy)}
                            onClone={() => onClone(strategy)}
                            onDelete={() => onDelete(strategy)}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};
